import * as React from 'react';

import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';

import { useCan } from '@/components/can/Can';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { AdminStaff } from '@/services/adminStaff/adminStaff.types';
import { AddStaffForm } from '../AddStaffForm';
import useDeleteAdminStaff from '../hooks/useDeleteAdminStaff';
import { getColumns } from './Column';
import useResetAdminStaffPassword from '../hooks/useResetAdminStaffPassword';
import useUpdateAdminStaffStatus from '../hooks/useUpdateAdminStaffStatus';

export function AdminStaffTable({
  data,
  isLoading = false,
  pagination,
}: {
  data: AdminStaff[];
  isLoading: boolean;
  pagination: Pagination;
}) {
  const { onOpen } = useDialogFormStore();
  const canAddNew = useCan({ allOf: [PERMS.staff.create] });
  const { openConfirmation } = useConfirmationStore();
  const { mutateAsync } = useDeleteAdminStaff();
  const { mutateAsync: resetPassword } = useResetAdminStaffPassword();
  const { mutateAsync: updateStatus } = useUpdateAdminStaffStatus();
  const { t } = useDynamicLocale();

  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );
  const columns = React.useMemo(
    () =>
      getColumns({
        openConfirmation,
        mutateAsync,
        t,
        onOpen,
        resetPassword,
        updateStatus,
      }),
    [openConfirmation, mutateAsync, resetPassword, t, onOpen, updateStatus],
  );
  const onAddNew = () => {
    onOpen({
      title: 'Add New Admin Staff',
      okText: t('common.page.modal.submit'),
      size: 'lg',
      modalTitle: 'common.page.modal.addNewStaff.title',
      component: <AddStaffForm />,
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    initialState: {
      sorting: [{ id: 'staffId', desc: true }],
    },
    meta: {
      title: t('userManagement.adminStaffs.table.title'),
      actionKey: 'onAddNew',
      action: canAddNew ? onAddNew : null,
      loading: isLoading,
      totalPage: pagination.totalItems,
    },
    getRowId: (originalRow) => String(originalRow.staffId),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table}>
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
